import {
  locale as localeEnv,
  pricesApiOverrideUrl
} from '@ecomm/data-env-variables'
import {
  D2DMightyValue,
  D2DPartnerGroup,
  D2DPartnerNameFieldForce
} from '@ecomm/door-to-door-constants/cookies'
import { logError } from '@ecomm/error-handling'
import type { TaskEither } from 'fp-ts/lib/TaskEither'
import * as TE from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/lib/function'
import { fetchApi } from './lib/fetchApi'
import type { ErrorResponse } from './lib/types'

export type GiftItemDTO = {
  readonly sku: string | null
  readonly title: string | null
  readonly quantity: number | null
  readonly value: number | null
}

export type GiftItemContainer = {
  readonly withMonitoring: GiftItemDTO | null
  readonly withoutMonitoring: GiftItemDTO | null
}

export type DynamicPriceThreshold = {
  readonly lowerThreshold: number
  readonly type: string
  readonly upperThreshold: number
}

export type DynamicGift = {
  readonly discountType: string
  readonly threshold: DynamicPriceThreshold
  readonly name: string
  readonly sku: string
  readonly isMatchingThreshold: boolean
}

export type DynamicDiscount = {
  readonly discountType: string
  readonly value: number
  /** Use type unions here for dynamic discounts that use something other than price as a threshold */
  readonly threshold: DynamicPriceThreshold
  readonly isMatchingThreshold: boolean
}

// https://github.com/simplisafe/ecomm-prices-api/blob/release/src/prices/models/price.dto.ts
export type PriceDiscounts = {
  /** The discounted price of the package without a service plan */
  readonly discountedPrice?: number
  /** The discounted price of the package with a service plan */
  readonly discountedPriceWithServicePlan?: number
  /** The absolute discount off a package without a service plan  */
  readonly absoluteDiscount?: number
  /** The absolute discount off a package with a service plan  */
  readonly absoluteDiscountWithServicePlan?: number
  /** The relative (percentage) discount off a package without a service plan  */
  readonly relativeDiscount?: number
  /** The relative (percentage) discount off a package with a service plan  */
  readonly relativeDiscountWithServicePlan?: number
  /** The gift items included without a monitoring plan */
  readonly withoutMonitoringGifts?: readonly GiftItemDTO[]
  /** the gift items included with a monitoring plan */
  readonly withMonitoringGifts?: readonly GiftItemDTO[]
  /** Tiers of Dynamic Discounting */
  readonly dynamicDiscounts?: readonly DynamicDiscount[]
  /** Tiers of Dynamic Gifts */
  readonly dynamicGifts?: readonly DynamicGift[]
}

/** Representation of the price of a given product */
export type PriceData = PriceDiscounts & {
  /** The internal ID of the product in CT */
  readonly id: string
  /** SKU of product */
  readonly sku: string
  /** Base price */
  readonly price: number
  /** Number of fraction digits used by locale */
  readonly fractionDigits: number
  /** The timestamp of when we last requested the current data for this product */
  readonly lastFetched: number
}

export type Prices = Record<string, PriceData>

export const formatPrices = (prices: readonly PriceData[]): Prices =>
  prices.reduce(
    (map, price) => ({
      ...map,
      [price.sku]: price
    }),
    {}
  )

export const isValidPromoResponse = (
  res: unknown
): res is readonly PriceData[] => {
  return (
    Array.isArray(res) &&
    res.some(item => item && typeof item === 'object' && 'price' in item)
  )
}

type CustomProps = {
  customerGroup: string | undefined
  partnerName: string | undefined
}
const getSearchParams = (
  skus: readonly string[],
  customProps: CustomProps
): URLSearchParams => {
  const searchParams = new URLSearchParams()

  skus.forEach(sku => searchParams.append('skus', sku))
  Object.entries(customProps).forEach(([key, value]) => {
    value && searchParams.append(key, value)
  })

  return searchParams
}

const getHeaders = (
  locale: 'en-US' | 'en-GB',
  extraHeaders: Record<string, string> = {}
): Record<string, string> => {
  return {
    Accept: 'application/json',
    'Accept-Language': locale,
    ...extraHeaders
  }
}

const getPriceApiEndpoint = (
  searchParams: URLSearchParams,
  pricesApiOverrideUrl?: string
) => {
  const endpoint = `${pricesApiOverrideUrl ? '/v1/prices' : '/prices/v1/prices'}?${searchParams.toString()}`

  return endpoint
}

export const fetchPrices = (
  skus: readonly string[],
  customerGroup?: string,
  partnerName?: string,
  extraHeaders: Record<string, string> = {}
): TaskEither<Error | ErrorResponse, Prices> => {
  if (
    customerGroup === D2DPartnerGroup &&
    partnerName !== D2DMightyValue.partnerName
  ) {
    partnerName = D2DPartnerNameFieldForce
  }

  const endpoint = pipe(
    skus,
    skus => getSearchParams(skus, { customerGroup, partnerName }),
    searchParams => getPriceApiEndpoint(searchParams, pricesApiOverrideUrl)
  )

  return pipe(
    TE.Do,
    // TODO (ECP-15287): Remove localeEnv and take it as a param
    TE.bind('locale', () => localeEnv),
    TE.chain(({ locale }) => {
      return fetchApi({
        baseUrlOverride: pricesApiOverrideUrl,
        endpoint,
        method: 'GET',
        headers: getHeaders(locale, extraHeaders)
      })
    }),
    TE.chain(res =>
      isValidPromoResponse(res) ? TE.right(formatPrices(res)) : TE.left(res)
    ),
    TE.mapError(error => {
      const errorMessage = Error(`Error fetching prices for ${skus.join(', ')}`)
      logError(errorMessage, { error: JSON.stringify(error) })

      return errorMessage
    })
  )
}
